import React, { useState, useEffect, useRef, useMemo } from 'react';
import NumInput from '../../../components/ui/NumInput';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useBusinessType } from '../../../context/BusinessTypeContext';
import {
  Save, Check, Building, Plus, Crown,
  Edit, X, ToggleLeft, ToggleRight, Clock, Info, Cake, Monitor,
  Coins, Percent, Star, ShoppingBag, AlertTriangle, Users, CheckCircle,
  MonitorSmartphone, Tablet, Megaphone, Receipt, ChevronRight
} from 'lucide-react';
import ImageUpload from '../../../components/common/ImageUpload';
import { v4 as uuidv4 } from 'uuid';
import type { Branch, SpecialDayRule } from '../../../types/user';
import {
  TIMEZONES,
  MEXICO_TIMEZONES,
  US_TIMEZONES,
  CANADA_TIMEZONES,
  detectBrowserTimeZone,
} from '../../../utils/dates';
import IntegratedQRCodePanel from './IntegratedQRCodePanel';
import QrCheckinCard from '../../../components/qr/QrCheckinCard';
import CardScanCard from '../../../components/qr/CardScanCard';
import { useError } from '../../../context/ErrorContext';
import ClientDataSection from './ClientDataSection';
import PhoneCountrySection from './PhoneCountrySection';
import PointsConfigSection from './PointsConfigSection';
import BalancePayToggle from './BalancePayToggle';
import TicketRefToggle from './TicketRefToggle';
import AccrualBlocksCard from './AccrualBlocksCard';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import ColorPickerSection from './ColorPickerSection';
import SpecialDaysCard from './SpecialDaysCard';
import { LegalSettingsSection } from '../../legal/pages/LegalSettingsPage';
import { clientsApi } from '../../../api/clientsApi';
import { normTag } from '../../../utils/cashbackEspecial';

interface BusinessFormProps {
  onSuccess?: () => void;
}

const DEFAULT_BUSINESS_NAME = "Mi Negocio";

interface TimeZoneObject {
  id: string;
  name: string;
  timeZone: string;
}

// F1 reorden (24-oct-2026, Carlos: «Mi negocio es un tuti fruti»): de 3 pestañas a 5, cada una UNA pregunta.
// La vieja «Programa de lealtad» juntaba 11 bloques (4,168 px en celular) y mezclaba cómo premio, cómo registro la
// visita y el corte diario; «Kiosco y promoción» repetía el toggle del QR. Se abre con ?tab=<id> (enlaces de otras
// pantallas); 'program' sigue aceptándose como alias de 'rewards'. Plan: forobeta-ops/docs/lealify-manager-reorden-menu-y-mi-negocio-plan-20261024.md
type BusinessTab = 'identity' | 'rewards' | 'visits' | 'kiosk' | 'promo';

const TABS: { id: BusinessTab; label: string; question: string; icon: React.ElementType }[] = [
  { id: 'identity', label: 'Identidad', question: '¿Quién soy?', icon: Building },
  { id: 'rewards', label: 'Cómo premio', question: '¿Qué gana mi cliente?', icon: Star },
  { id: 'visits', label: 'Cómo registro visitas', question: '¿Cómo cuento la visita?', icon: MonitorSmartphone },
  { id: 'kiosk', label: 'Kiosco', question: '¿Qué ve mi cliente?', icon: Tablet },
  { id: 'promo', label: 'Promoción', question: '¿Cómo lo doy a conocer?', icon: Megaphone },
];
const TAB_IDS = TABS.map(t => t.id) as string[];
const tabDesdeUrl = (v: string | null): BusinessTab => {
  if (v === 'program') return 'rewards';
  return (v && TAB_IDS.includes(v) ? v : 'identity') as BusinessTab;
};

const MODE_LABELS: Record<'visits' | 'cashback', string> = {
  visits: 'Puntos por Visita',
  cashback: 'Cashback (Dinero)',
};

// --- HELPERS ---

const formatTimeInZone = (date: Date, tz: string): string => {
  try {
    if (!tz || typeof tz !== 'string' || tz.trim() === '') {
      return new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).format(date);
    }
    return new Intl.DateTimeFormat('es-ES', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  } catch (error) {
    console.error(`Error al formatear hora para TZ "${tz}":`, error);
    return new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).format(date);
  }
};

const formatTimeZoneLabelForSelect = (tzObject: TimeZoneObject): string => {
  const ianaParts = tzObject.timeZone.split('/');
  const city = ianaParts[ianaParts.length - 1].replace(/_/g, ' ');
  let country = '';
  if (ianaParts.length > 1 && ianaParts[0] === "America") {
    if (MEXICO_TIMEZONES.some(mtz => mtz.timeZone === tzObject.timeZone)) country = "México";
    else if (US_TIMEZONES.some(utz => utz.timeZone === tzObject.timeZone)) country = "USA";
    else if (CANADA_TIMEZONES?.some(ctz => ctz.timeZone === tzObject.timeZone)) country = "Canadá";
  }
  const offsetMatch = tzObject.name.match(/\((UTC[+-]?\d+(:?\d{2})?|GMT[+-]?\d+(:?\d{2})?(-?\d+)?\/?(GMT[+-]?\d+)?)\)/i);
  const offsetString = offsetMatch ? ` ${offsetMatch[1]}` : '';
  return `${city}${country ? `, ${country}` : ''}${offsetString}`;
};

// Snapshot normalizado de sucursales para detectar cambios sin guardar
// (excluye createdAt: las fechas de Firestore no comparan estable por JSON)
const normalizeBranches = (list: Branch[] | undefined) =>
  (list || []).map(b => ({ id: b.id, name: b.name, isActive: b.isActive !== false, cashbackPercentage: b.cashbackPercentage ?? null }));

// Presets para selección rápida de puntos de referido

// --- COMPONENT ---

const BusinessForm: React.FC<BusinessFormProps> = ({ onSuccess }) => {
  const { currentUser, updateBusinessInfo, needsOnboarding, markOnboardingComplete } = useAuth();
  const { config } = useBusinessType();
  const { addError, addValidationError, handleFirebaseError, clearErrors } = useError();

  // State
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState<BusinessTab>(() => tabDesdeUrl(searchParams.get('tab')));
  const setActiveTab = (t: BusinessTab) => {
    setActiveTabState(t);
    const next = new URLSearchParams(searchParams);
    if (t === 'identity') next.delete('tab'); else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };
  // Enlace externo que cambia ?tab= con el formulario ya montado (p. ej. desde la FAQ).
  useEffect(() => {
    const t = tabDesdeUrl(searchParams.get('tab'));
    if (t !== activeTab) setActiveTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [businessName, setBusinessName] = useState(currentUser?.businessName || '');
  const [branches, setBranches] = useState<Branch[]>(currentUser?.branches || []);
  const [newBranchName, setNewBranchName] = useState('');
  const [primaryColor, setPrimaryColor] = useState(currentUser?.businessColors?.primary || '#006241');
  const [secondaryColor, setSecondaryColor] = useState(currentUser?.businessColors?.secondary || '#FFFFFF');

  const [pointsPerVisit, setPointsPerVisit] = useState(currentUser?.pointsPerVisit ?? 1);

  const [timeBetweenVisits, setTimeBetweenVisits] = useState(currentUser?.timeBetweenVisits ?? 60);
  const [allowPointsAccumulation, setAllowPointsAccumulation] = useState(currentUser?.allowPointsAccumulation !== false);

  const [loyaltyMode, setLoyaltyMode] = useState<'visits' | 'cashback'>(currentUser?.loyaltyMode || 'visits');
  const [cashbackPercentage, setCashbackPercentage] = useState<number>(currentUser?.cashbackPercentage ?? 10);
  // Cashback ESPECIAL por etiqueta (17-oct-2026): etiqueta vacía = apagado.
  const [specialTag, setSpecialTag] = useState<string>(currentUser?.cashbackSpecialTag || '');
  const [specialPct, setSpecialPct] = useState<number | null>(currentUser?.cashbackSpecialPercentage ?? null);
  const [specialDays, setSpecialDays] = useState<SpecialDayRule[]>(currentUser?.specialDays || []);   // días con premio extra (24-oct-2026)
  const [tagOptions, setTagOptions] = useState<string[]>([]);

  const [birthdayPointsMode, setBirthdayPointsMode] = useState<'disabled' | 'manual' | 'automatic'>(currentUser?.birthdayPointsMode || 'disabled');


  const [requestGender, setRequestGender] = useState(currentUser?.requestGender || false);
  const [requestBirthday, setRequestBirthday] = useState(currentUser?.requestBirthday || false);
  const [requestEmail, setRequestEmail] = useState(currentUser?.requestEmail || false);
  const [logoUrl, setLogoUrl] = useState(currentUser?.logoUrl || '');
  const [timeZone, setTimeZone] = useState(currentUser?.timeZone || 'America/Mexico_City');
  const [socialUrl, setSocialUrl] = useState(currentUser?.socialUrl || '');

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAddBranch, setShowAddBranch] = useState(false);
  const [showModeConfirm, setShowModeConfirm] = useState(false);

  const [browserTimeZone] = useState<string>(() => {
    const detectedValue = detectBrowserTimeZone();
    const tzEntry = TIMEZONES.find(tz => tz.id === detectedValue);
    if (tzEntry) return tzEntry.timeZone;
    try {
      new Intl.DateTimeFormat(undefined, { timeZone: detectedValue });
      return detectedValue;
    } catch (e) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
  });

  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [editingBranchId, setEditingBranchId] = useState<string | null>(null);
  const [editBranchName, setEditBranchName] = useState('');

  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [showOnboardingTip, setShowOnboardingTip] = useState(false);

  const branchSectionRef = useRef<HTMLDivElement>(null);
  const businessNameRef = useRef<HTMLInputElement>(null);

  // ✅ Usar helper centralizado — premium también pasa
  const hasProAccess = checkIsPro(currentUser?.subscription);

  // ✅ Sucursales contratadas: se lee de Firestore, default 1
  const maxBranches = currentUser?.maxBranches ?? 1;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (currentUser) {
      setBusinessName(currentUser.businessName || '');
      setBranches(currentUser.branches?.map(branch => ({ ...branch, isActive: branch.isActive !== false })) || []);
      setPrimaryColor(currentUser.businessColors?.primary || '#006241');
      setSecondaryColor(currentUser.businessColors?.secondary || '#FFFFFF');

      setPointsPerVisit(currentUser.pointsPerVisit ?? 1);
      setTimeBetweenVisits(currentUser.timeBetweenVisits ?? 60);
      setAllowPointsAccumulation(currentUser.allowPointsAccumulation !== false);

      setLoyaltyMode(currentUser.loyaltyMode || 'visits');
      setCashbackPercentage(currentUser.cashbackPercentage ?? 10);
      setSpecialTag(currentUser.cashbackSpecialTag || '');
      setSpecialPct(currentUser.cashbackSpecialPercentage ?? null);
      setSpecialDays(currentUser.specialDays || []);

      setBirthdayPointsMode(currentUser.birthdayPointsMode || 'disabled');

      setRequestGender(currentUser.requestGender || false);
      setRequestBirthday(currentUser.requestBirthday || false);
      setRequestEmail(currentUser.requestEmail || false);
      setLogoUrl(currentUser.logoUrl || '');
      setTimeZone(currentUser.timeZone || 'America/Mexico_City');
      setSocialUrl(currentUser.socialUrl || '');
    }
  }, [currentUser]);

  // Etiquetas existentes del negocio para sugerirlas en el cashback especial (best-effort: si falla, se escribe a mano).
  useEffect(() => {
    if (loyaltyMode !== 'cashback') return;
    let vivo = true;
    clientsApi.tags().then((t) => { if (vivo) setTagOptions(t.map((x) => x.name)); }).catch(() => {});
    return () => { vivo = false; };
  }, [loyaltyMode]);

  useEffect(() => {
      if (loyaltyMode === 'visits' && timeBetweenVisits === 0) {
          setTimeBetweenVisits(60);
      }
  }, [loyaltyMode]);

  useEffect(() => {
    setShowOnboardingTip(needsOnboarding);
  }, [needsOnboarding]);

  const isTimeZoneDifferent = browserTimeZone !== timeZone;
  const activeBranchesCount = branches.filter(b => b.isActive !== false).length;
  // ✅ Ahora el límite se basa en maxBranches (controlado desde Admin)
  const hasReachedBranchLimit = activeBranchesCount >= maxBranches;
  const isDefaultBusinessName = businessName.trim() === DEFAULT_BUSINESS_NAME;
  const shouldHighlightDefaultName = isDefaultBusinessName && hasAttemptedSave;

  const savedLoyaltyMode: 'visits' | 'cashback' = currentUser?.loyaltyMode || 'visits';
  const loyaltyModeChanged = loyaltyMode !== savedLoyaltyMode;

  // Detección de cambios sin guardar: compara el estado del formulario contra lo
  // persistido en currentUser, con la misma normalización que usa el guardado
  // (en cashback el tiempo entre visitas siempre se persiste como 0).
  // socialUrl se excluye: el panel de QR lo guarda por su cuenta.
  const savedSnapshot = useMemo(() => {
    if (!currentUser) return '';
    return JSON.stringify({
      businessName: currentUser.businessName || '',
      branches: normalizeBranches(currentUser.branches),
      primaryColor: currentUser.businessColors?.primary || '#006241',
      secondaryColor: currentUser.businessColors?.secondary || '#FFFFFF',
      pointsPerVisit: currentUser.pointsPerVisit ?? 1,
      timeBetweenVisits: (currentUser.loyaltyMode || 'visits') === 'cashback' ? 0 : (currentUser.timeBetweenVisits ?? 60),
      allowPointsAccumulation: currentUser.allowPointsAccumulation !== false,
      loyaltyMode: currentUser.loyaltyMode || 'visits',
      cashbackPercentage: currentUser.cashbackPercentage ?? 10,
      cashbackSpecialTag: currentUser.cashbackSpecialTag || '',
      cashbackSpecialPercentage: currentUser.cashbackSpecialPercentage ?? null,
      specialDays: currentUser.specialDays || [],
      birthdayPointsMode: currentUser.birthdayPointsMode || 'disabled',
      requestGender: currentUser.requestGender || false,
      requestBirthday: currentUser.requestBirthday || false,
      requestEmail: currentUser.requestEmail || false,
      logoUrl: currentUser.logoUrl || '',
      timeZone: currentUser.timeZone || 'America/Mexico_City',
    });
  }, [currentUser]);

  const currentSnapshot = JSON.stringify({
    businessName,
    branches: normalizeBranches(branches),
    primaryColor,
    secondaryColor,
    pointsPerVisit,
    timeBetweenVisits: loyaltyMode === 'cashback' ? 0 : timeBetweenVisits,
    allowPointsAccumulation,
    loyaltyMode,
    cashbackPercentage,
    cashbackSpecialTag: normTag(specialTag),
    cashbackSpecialPercentage: normTag(specialTag) ? specialPct : null,
    specialDays,
    birthdayPointsMode,
    requestGender,
    requestBirthday,
    requestEmail,
    logoUrl,
    timeZone,
  });

  const isDirty = savedSnapshot !== '' && currentSnapshot !== savedSnapshot;

  const otherTimeZones = useMemo(() => TIMEZONES.filter(tz =>
    !MEXICO_TIMEZONES.some(mtz => mtz.timeZone === tz.timeZone) &&
    !US_TIMEZONES.some(utz => utz.timeZone === tz.timeZone) &&
    (!CANADA_TIMEZONES || !CANADA_TIMEZONES.some(ctz => ctz.timeZone === tz.timeZone))
  ), []);

  // Labels dinámicos según modo de lealtad
  const unitLabel = loyaltyMode === 'cashback' ? 'pesos' : 'puntos';
  const unitPrefix = loyaltyMode === 'cashback' ? '$' : '';

  const handleAddBranch = () => {
    if (!newBranchName.trim()) { addValidationError('El nombre de la sucursal no puede estar vacío', 'branch-name-empty'); return; }
    if (branches.some(b => b.name.toLowerCase() === newBranchName.trim().toLowerCase())) { addValidationError('Ya existe una sucursal con ese nombre', 'branch-name-duplicate'); return; }
    // ✅ Validar contra maxBranches en vez de plan
    if (branches.filter(b => b.isActive !== false).length >= maxBranches) { addError(`Has alcanzado el límite de ${maxBranches} sucursal${maxBranches > 1 ? 'es' : ''} contratada${maxBranches > 1 ? 's' : ''}`, 'validation', 'branch-limit'); return; }
    const newBranch: Branch = { id: uuidv4(), name: newBranchName.trim(), createdAt: new Date(), isActive: true };
    setBranches([...branches, newBranch]);
    setNewBranchName('');
    setShowAddBranch(false);
  };

  const handleStartEditBranch = (branch: Branch) => { setEditingBranchId(branch.id); setEditBranchName(branch.name); };

  const handleSaveEditBranch = () => {
    if (!editBranchName.trim()) { addValidationError('El nombre de la sucursal no puede estar vacío', 'branch-name-empty-edit'); return; }
    if (branches.some(b => b.name.toLowerCase() === editBranchName.trim().toLowerCase() && b.id !== editingBranchId)) { addValidationError('Ya existe una sucursal con ese nombre', 'branch-name-duplicate-edit'); return; }
    setBranches(branches.map(branch => branch.id === editingBranchId ? { ...branch, name: editBranchName.trim() } : branch));
    setEditingBranchId(null); setEditBranchName('');
  };

  const handleCancelEditBranch = () => { setEditingBranchId(null); setEditBranchName(''); };

  const handleToggleBranchActive = (branchId: string) => {
    const branch = branches.find(b => b.id === branchId);
    // ✅ Validar contra maxBranches al reactivar
    if (branch?.isActive === false) { if (branches.filter(b => b.isActive !== false).length >= maxBranches) { addError(`Has alcanzado el límite de ${maxBranches} sucursal${maxBranches > 1 ? 'es' : ''} contratada${maxBranches > 1 ? 's' : ''}`, 'validation', 'branch-limit'); return; } }
    setBranches(branches.map(b => b.id === branchId ? { ...b, isActive: b.isActive === false } : b));
  };

  // Valida y, si el modelo de lealtad cambió, pide confirmación antes de persistir.
  const handleSave = () => {
    if (!currentUser?.businessId) { addError('No se pudo identificar el ID del negocio', 'validation', 'business-save'); return; }
    setHasAttemptedSave(true);
    if (!businessName.trim()) {
      addValidationError('El nombre del negocio es obligatorio', 'business-name-empty');
      setActiveTab('identity');
      setTimeout(() => businessNameRef.current?.focus(), 50);
      return;
    }
    if (businessName.trim() === DEFAULT_BUSINESS_NAME) {
      addValidationError('Debes cambiar el nombre genérico "Mi Negocio" por el nombre real de tu negocio', 'business-name-default');
      setActiveTab('identity');
      setTimeout(() => businessNameRef.current?.focus(), 50);
      return;
    }
    const activeCount = branches.filter(b => b.isActive !== false).length;
    if (activeCount === 0) {
      addValidationError('Debe tener al menos una sucursal activa', 'no-active-branches');
      setActiveTab('identity');
      setTimeout(() => branchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }

    if (loyaltyModeChanged) { setShowModeConfirm(true); return; }
    void performSave();
  };

  const performSave = async () => {
    try {
      setLoading(true); clearErrors();

      const finalTimeBetweenVisits = loyaltyMode === 'cashback' ? 0 : timeBetweenVisits;

      const businessData = {
          businessName,
          branches,
          businessColors: { primary: primaryColor, secondary: secondaryColor },
          pointsPerVisit,
          timeBetweenVisits: finalTimeBetweenVisits,
          allowPointsAccumulation,
          loyaltyMode,
          cashbackPercentage,
          // Se guarda normalizada (igual que la compara el servidor); sin etiqueta = apagado.
          cashbackSpecialTag: normTag(specialTag) || null,
          cashbackSpecialPercentage: normTag(specialTag) ? specialPct : null,
          specialDays,
          birthdayPointsMode,
          requestGender,
          requestBirthday,
          requestEmail,
          logoUrl,
          timeZone
          // socialUrl se excluye a propósito: el panel de QR (IntegratedQRCodePanel)
          // lo persiste por su cuenta vía PUT /biz/settings (tenant_settings.social).
      };
      await updateBusinessInfo(currentUser!.businessId!, businessData);
      if (needsOnboarding && businessName.trim() && businessName.trim() !== DEFAULT_BUSINESS_NAME) { await markOnboardingComplete(); }
      setSaveSuccess(true); setTimeout(() => { setSaveSuccess(false); if (onSuccess) onSuccess(); }, 2000);
    } catch (error) { console.error('Error saving business:', error); handleFirebaseError(error, 'business-save');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      {showOnboardingTip && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10 border-l-4 border-blue-400 p-6 mb-6 rounded-md shadow-lg animate-fade-in-up">
           <div className="flex">
            <div className="flex-shrink-0 pt-0.5"><div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-500/20 text-accent text-2xl">🎉</div></div>
            <div className="ml-4 flex-1">
              <h3 className="text-xl font-semibold text-blue-800 dark:text-blue-300">¡Bienvenido a Lealify! ✨</h3>
              <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">Vamos a configurar tu negocio...</p>
            </div>
          </div>
        </div>
      )}

      {/* Pestañas: una pregunta cada una. En celular, rejilla de 2 columnas con las 5 A LA VISTA (la barra con
          scroll escondía la tercera y nada indicaba que había más); en escritorio, barra de pestañas. */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-1 sm:border-b sm:border-line" role="tablist" data-testid="business-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 text-left transition-colors rounded-lg border px-3 py-2 last:col-span-2 sm:last:col-span-1 sm:rounded-none sm:rounded-t-md sm:border-0 sm:border-b-2 sm:px-4 sm:pt-2 sm:pb-2.5 sm:-mb-px text-sm font-semibold ${
                isActive
                  ? 'border-accent bg-accent/10 text-accent sm:bg-transparent'
                  : 'border-line text-content-2 hover:text-content hover:bg-surface-2 sm:border-transparent'
              }`}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="leading-tight">
                {tab.label}
                <span className="block text-[11px] font-normal text-content-3">{tab.question}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* ============ PESTAÑA 1: IDENTIDAD ============ */}
      <div className={activeTab === 'identity' ? 'space-y-6 animate-fade-in-up' : 'hidden'}>
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-line">
            <h3 className="text-lg font-medium text-content">Datos del negocio</h3>
            <p className="mt-1 text-sm text-content-3">Nombre, logo, colores y zona horaria — se configura una vez</p>
          </div>
          <div className="px-4 py-5 sm:p-6 space-y-6">
            {/* Nombre */}
            <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-content-2 mb-1">Nombre del Negocio <span className="text-red-500">*</span></label>
                <input id="businessName" ref={businessNameRef} type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)} className={`shadow-sm focus:ring-accent focus:border-accent block w-full sm:text-sm border-line rounded-md ${shouldHighlightDefaultName ? 'border-red-300' : ''}`} placeholder="Nombre de tu negocio" required />
            </div>

            {/* Logo */}
            {/* sin `folder="logos"`: ImageUpload no tiene esa prop — sube siempre al mismo
                endpoint (api.lealify.com/upload con token), la carpeta la decide el server. */}
            <div><ImageUpload initialImageUrl={logoUrl} onImageChange={setLogoUrl} title="Logo del Negocio" description="Sube el logo de tu negocio o ingresa una URL" /></div>

            {/* Colores con preview */}
            <ColorPickerSection
              primaryColor={primaryColor}
              setPrimaryColor={setPrimaryColor}
              secondaryColor={secondaryColor}
              setSecondaryColor={setSecondaryColor}
              businessName={businessName}
            />

            {/* Zona Horaria */}
            <div>
                <label htmlFor="timeZone" className="block text-sm font-medium text-content-2 mb-1">Zona Horaria</label>
                <div className="mt-1">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Clock className="h-5 w-5 text-content-3" /></div>
                        <select id="timeZone" value={timeZone} onChange={(e) => setTimeZone(e.target.value)} className="shadow-sm focus:ring-accent focus:border-accent block w-full pl-10 pr-3 py-2 sm:text-sm border-line rounded-md">
                           <optgroup label="México">{MEXICO_TIMEZONES.map(tz => (<option key={tz.id} value={tz.timeZone}>{formatTimeZoneLabelForSelect(tz)}</option>))}</optgroup>
                           <optgroup label="Estados Unidos">{US_TIMEZONES.map(tz => (<option key={tz.id} value={tz.timeZone}>{formatTimeZoneLabelForSelect(tz)}</option>))}</optgroup>
                           {CANADA_TIMEZONES && <optgroup label="Canadá">{CANADA_TIMEZONES.map(tz => (<option key={tz.id} value={tz.timeZone}>{formatTimeZoneLabelForSelect(tz)}</option>))}</optgroup>}
                           {otherTimeZones.length > 0 && <optgroup label="Otros">{otherTimeZones.map(tz => (<option key={tz.id} value={tz.timeZone}>{formatTimeZoneLabelForSelect(tz)}</option>))}</optgroup>}
                        </select>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-content-3">
                        <Clock className="flex-shrink-0 mr-1.5 h-4 w-4 text-content-3" />
                        <span>Hora actual: {formatTimeInZone(currentTime, timeZone)}{isTimeZoneDifferent && (<span className="ml-1 text-xs text-blue-500">(Tu zona horaria: {formatTimeInZone(currentTime, browserTimeZone)})</span>)}</span>
                    </div>
                </div>
            </div>
          </div>
        </div>

        {/* Sucursales */}
        <div className="bg-surface shadow rounded-lg overflow-hidden" ref={branchSectionRef}>
          <div className="px-4 py-5 sm:px-6 border-b border-line flex justify-between items-center gap-3">
            <div>
              <h3 className="text-lg font-medium text-content">Sucursales <span className="text-red-500">*</span></h3>
              <p className="mt-1 text-sm text-content-3">Activas: {activeBranchesCount} de {maxBranches} contratada{maxBranches > 1 ? 's' : ''}</p>
            </div>
            <button type="button" onClick={() => setShowAddBranch(true)} disabled={hasReachedBranchLimit} className={`inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent ${hasReachedBranchLimit ? 'opacity-50 cursor-not-allowed' : ''}`}><Plus className="h-4 w-4 mr-1" />Añadir Sucursal</button>
          </div>
          <div className="px-4 py-5 sm:p-6">
                {showAddBranch && (
                    <div className="mb-3 flex items-center space-x-2 p-2 bg-surface-2 rounded-md">
                        <input type="text" value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)} placeholder="Nombre de la sucursal" className="block w-full sm:text-sm border-line rounded-md" />
                        <button type="button" onClick={handleAddBranch} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-accent-solid hover:bg-accent-solid/90">Guardar</button>
                        <button type="button" onClick={() => { setShowAddBranch(false); setNewBranchName(''); }} className="inline-flex items-center px-3 py-1.5 border border-line text-xs font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2">Cancelar</button>
                    </div>
                )}
                <div className="space-y-2">
                    {branches.length === 0 ? (<div className="text-center p-4 bg-surface-2 rounded-md border border-line"><p className="text-content-3 text-sm">No hay sucursales configuradas.</p></div>) : (branches.map(branch => (
                        <div key={branch.id} className={`flex justify-between items-center p-2 rounded-md border ${branch.isActive !== false ? 'border-line bg-surface' : 'border-line bg-surface-2'}`}>
                            {editingBranchId === branch.id ? (
                                <div className="flex-1 flex items-center space-x-2">
                                    <input type="text" value={editBranchName} onChange={(e) => setEditBranchName(e.target.value)} className="block w-full sm:text-sm border-line rounded-md" />
                                    <button type="button" onClick={handleSaveEditBranch} className="inline-flex items-center p-1 border border-transparent rounded-md text-white bg-accent-solid hover:bg-accent-solid/90"><Check className="h-4 w-4" /></button>
                                    <button type="button" onClick={handleCancelEditBranch} className="inline-flex items-center p-1 border border-line rounded-md text-content-2 bg-surface hover:bg-surface-2"><X className="h-4 w-4" /></button>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center">
                                        <Building className={`h-4 w-4 mr-2 ${branch.isActive !== false ? 'text-blue-500' : 'text-content-3'}`} />
                                        <span className={`font-medium ${branch.isActive !== false ? 'text-content' : 'text-content-3'}`}>{branch.name}</span>
                                        {branch.isActive === false && (<span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-canvas text-content">Inactiva</span>)}
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <button type="button" onClick={() => handleStartEditBranch(branch)} className="inline-flex items-center p-1 border border-line rounded-md text-content-2 bg-surface hover:bg-surface-2"><Edit className="h-3 w-3" /></button>
                                        <button type="button" onClick={() => handleToggleBranchActive(branch.id)} className="inline-flex items-center p-1 border border-line rounded-md text-content-2 bg-surface hover:bg-surface-2" title={branch.isActive !== false ? "Desactivar sucursal" : "Activar sucursal"}>
                                            {branch.isActive !== false ? (<ToggleRight className="h-3 w-3 text-green-600 dark:text-green-400" />) : (<ToggleLeft className="h-3 w-3 text-content-2" />)}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )))}
                </div>
                {/* ✅ Texto dinámico según sucursales contratadas */}
                {branches.length > 0 && (
                  <p className="mt-2 text-xs text-content-3">
                    Las sucursales inactivas no aparecerán en el sistema de registro de clientes.
                    {hasReachedBranchLimit && hasProAccess && (
                      <span className="block mt-1 text-amber-600 dark:text-amber-400">Para agregar más sucursales, contacta al administrador.</span>
                    )}
                    {hasReachedBranchLimit && !hasProAccess && (
                      <span className="block mt-1 text-amber-600 dark:text-amber-400">
                        <Crown className="h-3 w-3 inline-block mr-0.5 text-amber-500" />
                        Actualiza a PRO para contratar sucursales adicionales.
                      </span>
                    )}
                  </p>
                )}
          </div>
        </div>
      </div>

      {/* ============ PESTAÑA 2: CÓMO PREMIO (modelo, %, etiqueta, cumpleaños; referidos → su pantalla) ============ */}
      <div className={activeTab === 'rewards' ? 'space-y-6 animate-fade-in-up' : 'hidden'} data-testid="panel-rewards">
        {/* Modelo de Lealtad */}
        <div className="bg-surface p-5 rounded-lg border border-line shadow-sm">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <div className="flex items-center">
                  <Coins className="h-5 w-5 text-accent mr-2" />
                  <h3 className="text-lg font-medium text-content">Modelo de Lealtad</h3>
                </div>
              </div>
              <p className="text-sm text-content-3 mb-4">Define cómo acumulan <strong>todos</strong> tus clientes.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${loyaltyMode === 'visits' ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 ring-1 ring-blue-500' : 'border-line hover:border-blue-300'}`}
                  onClick={() => setLoyaltyMode('visits')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-content flex items-center"><Star className="w-4 h-4 mr-2 text-yellow-500" /> Puntos por Visita</span>
                    {loyaltyMode === 'visits' && <Check className="w-5 h-5 text-accent" />}
                  </div>
                  <p className="text-xs text-content-3">Otorga una cantidad fija de puntos cada vez que el cliente visita tu negocio. Ideal para cafeterías, gimnasios o servicios rápidos.</p>
                </div>

                <div
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${loyaltyMode === 'cashback' ? 'border-green-500 bg-green-50 dark:bg-green-500/10 ring-1 ring-green-500' : 'border-line hover:border-green-300'}`}
                  onClick={() => setLoyaltyMode('cashback')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-content flex items-center"><Percent className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" /> Cashback (Dinero)</span>
                    {loyaltyMode === 'cashback' && <Check className="w-5 h-5 text-green-600 dark:text-green-400" />}
                  </div>
                  <p className="text-xs text-content-3">Otorga un porcentaje del monto de compra como saldo a favor. Ideal para restaurantes, tiendas o spas con tickets variables.</p>
                </div>
              </div>

              {loyaltyModeChanged && (
                <div className="mb-4 flex items-start gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md p-3 text-sm text-amber-700 dark:text-amber-400 animate-fade-in-up">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Estás cambiando el modelo de <strong>{MODE_LABELS[savedLoyaltyMode]}</strong> a <strong>{MODE_LABELS[loyaltyMode]}</strong>. Esto afecta cómo acumulan todos tus clientes; te pediremos confirmación al guardar.</span>
                </div>
              )}

              {loyaltyMode === 'visits' ? (
                 <PointsConfigSection
                    pointsPerVisit={pointsPerVisit} setPointsPerVisit={setPointsPerVisit}
                    timeBetweenVisits={timeBetweenVisits} setTimeBetweenVisits={setTimeBetweenVisits}
                    allowPointsAccumulation={allowPointsAccumulation} setAllowPointsAccumulation={setAllowPointsAccumulation}
                 />
              ) : (
                 <div className="bg-surface p-4 rounded border border-green-200 dark:border-green-500/30 space-y-4 animate-fade-in-up">
                    <div>
                        <label htmlFor="cashbackPercentage" className="block text-sm font-medium text-content-2">Porcentaje de Retorno (%)</label>
                        <div className="mt-1 relative rounded-md shadow-sm max-w-xs">
                          <NumInput
                            name="cashbackPercentage"
                            id="cashbackPercentage"
                            className="focus:ring-green-500 focus:border-green-500 block w-full pl-3 pr-10 sm:text-sm border-line rounded-md"
                            placeholder="10"
                            min="0.5"
                            max="100"
                            step="0.5"
                            inputMode="decimal"
                            value={cashbackPercentage}
                            onValue={setCashbackPercentage}
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-content-3 sm:text-sm">%</span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-content-3">Ejemplo: Si pones 10%, por cada $100 pesos de compra, el cliente recibe $10 pesos (10 puntos).</p>
                    </div>

                    {/* % por sucursal (8-oct-2026): negocios con dos conceptos bajo una cuenta (súper + café)
                        quieren un % distinto en cada uno. Vacío = usa el general de arriba. El saldo
                        sigue siendo UNO: se gana con el % de donde compró y se usa en cualquiera. */}
                    {branches.filter(b => b.isActive !== false).length > 1 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-content-2">Porcentaje por sucursal <span className="font-normal text-content-3">(opcional)</span></p>
                        <p className="text-xs text-content-3">Déjalo vacío para usar el {cashbackPercentage}% general. Útil si una sucursal tiene otro margen (p. ej. abarrotes 2%, cafetería 8%). El saldo del cliente es uno solo y lo usa en cualquier sucursal.</p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {branches.filter(b => b.isActive !== false).map(b => (
                            <label key={b.id} className="flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2">
                              <span className="text-sm text-content truncate">{b.name}</span>
                              <span className="relative w-24 shrink-0">
                                <input
                                  type="number" min="0" max="100" step="0.5" inputMode="decimal"
                                  aria-label={`Porcentaje de ${b.name}`}
                                  placeholder={String(cashbackPercentage)}
                                  className="block w-full pl-2 pr-7 py-1 sm:text-sm border-line rounded-md focus:ring-green-500 focus:border-green-500"
                                  value={b.cashbackPercentage ?? ''}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    const v = raw === '' ? null : Math.min(100, Math.max(0, Number(raw)));
                                    setBranches(branches.map(x => x.id === b.id ? { ...x, cashbackPercentage: v } : x));
                                  }}
                                />
                                <span className="absolute inset-y-0 right-2 flex items-center text-content-3 text-sm pointer-events-none">%</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cashback ESPECIAL por etiqueta (17-oct-2026, Cenizas del Sol): los clientes con la etiqueta
                        ganan este % si es mayor que el general (o el de su sucursal). Vacío = apagado. */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-content-2">Cashback especial por etiqueta <span className="font-normal text-content-3">(opcional)</span></p>
                      <p className="text-xs text-content-3">Los clientes que tengan esta etiqueta ganan el % especial en lugar del {cashbackPercentage}% general (si el especial es mayor). Déjalo vacío para no usarlo. La etiqueta se pone en la ficha del cliente, sección «Etiquetas».</p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="block">
                          <span className="block text-xs text-content-3 mb-1">Etiqueta</span>
                          <input
                            type="text" list="cashback-special-tags" maxLength={60}
                            data-testid="cashback-special-tag"
                            placeholder="cashback_especial"
                            className="block w-full px-3 py-1.5 sm:text-sm border-line rounded-md focus:ring-green-500 focus:border-green-500"
                            value={specialTag}
                            onChange={(e) => setSpecialTag(e.target.value)}
                          />
                          <datalist id="cashback-special-tags">
                            {tagOptions.map((t) => <option key={t} value={t} />)}
                          </datalist>
                        </label>
                        <label className="block">
                          <span className="block text-xs text-content-3 mb-1">% especial</span>
                          <span className="relative block max-w-[8rem]">
                            <input
                              type="number" min="0" max="100" step="0.5" inputMode="decimal"
                              data-testid="cashback-special-pct"
                              placeholder="7"
                              className="block w-full pl-3 pr-7 py-1.5 sm:text-sm border-line rounded-md focus:ring-green-500 focus:border-green-500"
                              value={specialPct ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setSpecialPct(raw === '' ? null : Math.min(100, Math.max(0, Number(raw))));
                              }}
                            />
                            <span className="absolute inset-y-0 right-2 flex items-center text-content-3 text-sm pointer-events-none">%</span>
                          </span>
                        </label>
                      </div>
                      {normTag(specialTag) && specialPct == null && (
                        <p className="text-xs text-amber-600 dark:text-amber-400">Falta el % especial: sin él no se aplica.</p>
                      )}
                    </div>

                    <div className="pt-4 border-t border-line">
                        <div className="flex items-center h-full">
                           <input id="allowAccumulationCashback" type="checkbox" checked={allowPointsAccumulation} onChange={(e) => setAllowPointsAccumulation(e.target.checked)} className="focus:ring-green-500 h-4 w-4 text-green-600 dark:text-green-400 border-line rounded" />
                           <label htmlFor="allowAccumulationCashback" className="ml-2 block text-sm text-content">Permitir acumular saldo</label>
                        </div>
                        <p className="mt-2 text-xs text-content-3">Cómo se captura la compra (monto, pagar con saldo, folio del ticket) está en la pestaña <button type="button" onClick={() => setActiveTab('visits')} className="text-accent hover:underline font-medium">Cómo registro visitas</button>.</p>
                    </div>
                 </div>
              )}
        </div>

        {/* Días con premio extra (24-oct-2026, Hogar China City): martes x1.5, Buen Fin x2… */}
        <SpecialDaysCard
          rules={specialDays} onChange={setSpecialDays}
          loyaltyMode={loyaltyMode === 'cashback' ? 'cashback' : 'visits'}
          cashbackPercentage={Number(cashbackPercentage) || 0} pointsPerVisit={Number(pointsPerVisit) || 0}
          hasPro={hasProAccess}
        />

        {/* Referidos (F3 reorden 24-oct-2026): sus ajustes viven en Referidos → Ajustes, junto al historial y al mensaje. */}
        <RouterLink to="/dashboard/referrals?tab=ajustes" data-testid="puntero-referidos" className="flex items-center gap-3 bg-surface p-4 rounded-lg border border-line shadow-sm hover:bg-surface-2 transition-colors">
          <Users className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-medium text-content">Referidos</span>
            <span className="block text-xs text-content-3">
              {currentUser?.referralsEnabled
                ? <>Encendidos: {unitPrefix}{currentUser?.pointsPerReferral ?? 0} {unitLabel} a quien recomienda{(currentUser?.referralBonusForNewClient ?? 0) > 0 ? <> y {unitPrefix}{currentUser?.referralBonusForNewClient} de bienvenida al nuevo</> : null}. Se ajustan en Referidos → Ajustes.</>
                : <>Apagados. Se encienden y configuran en Referidos → Ajustes.</>}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-content-3 flex-shrink-0" />
        </RouterLink>

        {/* Sección Cumpleaños */}
        <div className="bg-surface p-4 rounded-lg border border-line shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-content flex items-center">
                            <Cake className="h-4 w-4 mr-2 text-pink-500" />
                            Puntos Dobles por Cumpleaños
                        </span>
                        <span className="text-xs text-content-3 mt-1">
                            Define si el sistema duplica los puntos de la visita el día del cumpleaños del cliente.
                        </span>
                    </div>
                    <div className="w-full sm:w-auto">
                        <select
                            value={birthdayPointsMode}
                            onChange={(e) => setBirthdayPointsMode(e.target.value as 'disabled' | 'manual' | 'automatic')}
                            className="block w-full sm:w-48 rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
                        >
                            <option value="disabled">Desactivado</option>
                            <option value="manual">Manual (Botón opcional)</option>
                            <option value="automatic">Automático (Aplicar siempre)</option>
                        </select>
                    </div>
                </div>

                <div className="mt-3 text-xs text-content-3 bg-surface-2 p-3 rounded border border-line">
                    <p className="mb-2 font-medium text-content-2">Comportamiento en el Panel de Administración:</p>
                    <ul className="list-disc pl-4 space-y-1 mb-3">
                        {birthdayPointsMode === 'disabled' && <li>Solo verás una alerta visual para felicitar al cliente.</li>}
                        {birthdayPointsMode === 'manual' && <li>Verás un botón para duplicar los puntos con un solo clic (tú decides si usarlo).</li>}
                        {birthdayPointsMode === 'automatic' && <li>El sistema calculará el doble de puntos automáticamente al ingresar el monto.</li>}
                    </ul>

                    {/* Verdad verificada en backend (2026-08-31): en 'automatic' el multiplicador
                        aplica en CUALQUIER registro de visita (kiosco incluido); lo único exclusivo
                        de este panel es el BOTÓN del modo 'manual'. El copy viejo decía lo contrario. */}
                    {birthdayPointsMode !== 'disabled' && (
                        <p className="text-amber-600 dark:text-amber-400 flex items-center border-t border-line pt-2 mt-2">
                            <Info className="w-3 h-3 mr-1 min-w-[12px]" />
                            {birthdayPointsMode === 'automatic'
                                ? <span> Nota: en Automático el doble se aplica en <strong>todos los registros de visita, Kiosco (Tablet) incluido</strong> — solo necesita que el cumpleaños esté capturado en la ficha del cliente. </span>
                                : <span> Nota: el botón para duplicar del modo Manual <strong>solo existe en este panel</strong>; en el Kiosco (Tablet) los clientes reciben sus puntos normales. </span>}
                        </p>
                    )}
                </div>
        </div>
      </div>

      {/* ============ PESTAÑA 3: CÓMO REGISTRO VISITAS (kiosco / QR / tarjeta, captura de la compra, quién no acumula) ============ */}
      <div className={activeTab === 'visits' ? 'space-y-6 animate-fade-in-up' : 'hidden'} data-testid="panel-visits">
        {/* ¿Cómo registran sus visitas? (8-oct-2026): QR y tarjeta se encienden SOLO aquí (antes también en
            «Kiosco y promoción» y en la propia pantalla Visitas por QR, que ahora solo enlaza). */}
        <div className="bg-surface p-5 rounded-lg border border-line shadow-sm">
          <div className="flex items-center mb-1">
            <Monitor className="h-5 w-5 text-accent mr-2" />
            <h3 className="text-lg font-medium text-content">¿Cómo registran sus visitas tus clientes?</h3>
          </div>
          <p className="text-sm text-content-3 mb-4">Puedes combinar los que quieras; los tres suman al mismo programa.</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-xl border-2 border-[#0057B8] bg-[#0057B8]/5 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#0057B8]/10 text-[#0057B8] dark:text-blue-300"><Monitor className="h-5 w-5" /></span>
                  <div>
                    <p className="font-semibold text-content">Kiosco en tablet o celular</p>
                    <p className="text-xs text-content-3 mt-0.5">Lo pones en tu mostrador y tu cliente teclea su número al pagar.</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <span className="text-[10px] font-bold uppercase tracking-wide bg-[#0057B8] text-white px-2 py-0.5 rounded flex-shrink-0">Siempre activo</span>
                <a href="https://app.lealify.com" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-accent hover:underline">Abrir el kiosco ↗</a>
              </div>
            </div>
            <QrCheckinCard variante="compacta" />
            <CardScanCard variante="compacta" />
          </div>
        </div>

        {/* Cómo se captura la compra: solo tiene sentido en cashback (en puntos la visita es la visita). */}
        {loyaltyMode === 'cashback' && (
          <div className="bg-surface p-5 rounded-lg border border-line shadow-sm space-y-4" data-testid="captura-compra">
            <div>
              <div className="flex items-center mb-1">
                <Receipt className="h-5 w-5 text-accent mr-2" />
                <h3 className="text-lg font-medium text-content">Cómo se captura la compra</h3>
              </div>
              <p className="text-sm text-content-3">Lo que el cajero teclea al registrar una visita con cashback.</p>
            </div>
            <div className="bg-blue-50 dark:bg-blue-500/10 border-l-4 border-blue-400 p-4 rounded-md">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <Info className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">Modo Cajero / Ventas Múltiples</h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                            <p>
                                En cashback no hay tiempo mínimo entre visitas (0 min): el cajero puede registrar varias ventas seguidas al mismo cliente.
                            </p>
                            <p className="mt-1 font-semibold flex items-center">
                                <ShoppingBag className="w-3 h-3 mr-1" />
                                El Kiosco solicitará el "Monto de Venta" al registrar la visita.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            {/* «Usar saldo» a monto libre (8-oct-2026): solo con cashback YA guardado — el backend lo rechaza en puntos. */}
            {savedLoyaltyMode === 'cashback' && <BalancePayToggle />}
            <TicketRefToggle />
          </div>
        )}

        {/* «Quién no acumula» (19-oct-2026, #1465 Silvestre): excepciones al registro de visitas. */}
        <AccrualBlocksCard />
      </div>

      {/* ============ PESTAÑA 4: KIOSCO (qué ve el cliente al registrarse: datos, lada, términos) ============ */}
      <div className={activeTab === 'kiosk' ? 'space-y-6 animate-fade-in-up' : 'hidden'} data-testid="panel-kiosk">
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-line">
            <h3 className="text-lg font-medium text-content">Registro de clientes en el kiosco</h3>
            <p className="mt-1 text-sm text-content-3">Qué datos pide el kiosco cuando un {config.clientLabel.toLowerCase()} se registra por primera vez</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <ClientDataSection requestGender={requestGender} setRequestGender={setRequestGender} requestBirthday={requestBirthday} setRequestBirthday={setRequestBirthday} requestEmail={requestEmail} setRequestEmail={setRequestEmail} clientLabel={config.clientLabel} />
            <p className="mt-3 text-xs text-content-3 flex items-start">
              <Info className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
              Cada dato extra hace el registro un poco más lento en el mostrador. Pide solo lo que vayas a usar.
            </p>
            {/* País del teléfono (F2, 20-ago-2026): autocontenida como IntegratedQRCodePanel —
                guarda sola en tenant_settings.kiosk, no pasa por la barra de guardado. */}
            <PhoneCountrySection />
          </div>
        </div>

        {/* Términos legales (F1 reorden 24-oct-2026): antes pantalla propia en el menú; es lo que acepta el cliente al
            registrarse en el kiosco. Se guarda con su propio botón (legalApi), como la lada y el QR promocional. */}
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <LegalSettingsSection />
          </div>
        </div>
      </div>

      {/* ============ PESTAÑA 5: PROMOCIÓN (códigos QR hacia tu sitio / redes) ============ */}
      <div className={activeTab === 'promo' ? 'space-y-6 animate-fade-in-up' : 'hidden'} data-testid="panel-promo">
        {currentUser?.businessId && (<IntegratedQRCodePanel businessId={currentUser.businessId} socialUrl={socialUrl} onSocialUrlChange={setSocialUrl} />)}
      </div>

      {/* Barra de guardado fija: siempre visible, avisa de cambios pendientes. En Promoción el panel de QR guarda
          solo, así que la barra únicamente aparece si quedaron cambios de otra pestaña. */}
      <div className={activeTab === 'promo' && !isDirty && !saveSuccess ? 'hidden' : 'sticky bottom-0 z-30 pt-2'}>
        <div className="bg-surface border border-line rounded-lg shadow-lg px-4 py-3 flex items-center justify-between gap-3">
          {saveSuccess ? (
            <span className="flex items-center text-sm font-medium text-green-600 dark:text-green-400">
              <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" /> Los cambios se guardaron correctamente
            </span>
          ) : isDirty ? (
            <span className="flex items-center text-sm font-medium text-amber-600 dark:text-amber-400">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse mr-2 flex-shrink-0" /> Tienes cambios sin guardar
            </span>
          ) : (
            <span className="text-sm text-content-3">Sin cambios pendientes</span>
          )}
          <button onClick={handleSave} disabled={loading || !businessName.trim() || branches.length === 0} className={`flex-shrink-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-solid hover:bg-accent-solid/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 ${needsOnboarding ? 'animate-pulse' : ''}`}>
              <Save className="h-4 w-4 mr-2" />
              {loading ? 'Guardando...' : (needsOnboarding ? `✅ Guardar y Activar Funciones` : 'Guardar Cambios')}
          </button>
        </div>
      </div>

      {/* Confirmación de cambio de modelo de lealtad */}
      {showModeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="modal-overlay absolute inset-0 bg-black/50" onClick={() => setShowModeConfirm(false)} />
          <div className="relative bg-surface rounded-lg shadow-xl max-w-md w-full p-6 modal-panel">
            <div className="flex items-start">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-amber-100 dark:bg-amber-500/20">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-base font-semibold text-content">¿Cambiar el modelo de lealtad?</h3>
                <p className="mt-2 text-sm text-content-2">
                  Vas a cambiar de <strong>{MODE_LABELS[savedLoyaltyMode]}</strong> a <strong>{MODE_LABELS[loyaltyMode]}</strong>.
                  Esto cambia cómo acumulan <strong>todos tus clientes registrados</strong> a partir de ahora (sus saldos actuales se conservan).
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setShowModeConfirm(false)} className="inline-flex items-center px-4 py-2 border border-line rounded-md text-sm font-medium text-content-2 bg-surface hover:bg-surface-2">
                Cancelar
              </button>
              <button type="button" onClick={() => { setShowModeConfirm(false); void performSave(); }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-solid hover:bg-accent-solid/90">
                Sí, cambiar y guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessForm;
